import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const propertyIdFromUrl = searchParams.get('property');

  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(propertyIdFromUrl || '');
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState(2);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);

  const fetchProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  }, []);

  const fetchAvailability = useCallback(async () => {
    if (!selectedProperty) return;
    
    try {
      const startDate = new Date().toISOString().split('T')[0];
      const endDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const response = await axios.get(
        `${API}/bookings/availability/${selectedProperty}?start_date=${startDate}&end_date=${endDate}`
      );
      setBookedDates(response.data.booked_dates);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  }, [selectedProperty]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    if (selectedProperty) {
      fetchAvailability();
    }
  }, [selectedProperty, fetchAvailability]);

  const isDateBooked = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return bookedDates.some((booking) => {
      return dateStr >= booking.check_in && dateStr <= booking.check_out;
    });
  };

  const tileDisabled = ({ date }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;
    return isDateBooked(date);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedProperty) {
      toast.error(t('booking.errors.selectProperty'));
      return;
    }
    if (!checkIn) {
      toast.error(t('booking.errors.selectCheckIn'));
      return;
    }
    if (!checkOut) {
      toast.error(t('booking.errors.selectCheckOut'));
      return;
    }
    if (checkOut <= checkIn) {
      toast.error(t('booking.errors.invalidDates'));
      return;
    }
    if (!guestName.trim() || !guestEmail.trim() || !guestPhone.trim()) {
      toast.error(t('booking.errors.fillFields'));
      return;
    }

    setLoading(true);

    try {
      const bookingData = {
        property_id: selectedProperty,
        check_in: checkIn.toISOString().split('T')[0],
        check_out: checkOut.toISOString().split('T')[0],
        guests,
        guest_name: guestName,
        guest_email: guestEmail,
        guest_phone: guestPhone,
        special_requests: specialRequests
      };

      await axios.post(`${API}/bookings`, bookingData);
      
      toast.success(t('booking.success'));
      
      setCheckIn(null);
      setCheckOut(null);
      setGuests(2);
      setGuestName('');
      setGuestEmail('');
      setGuestPhone('');
      setSpecialRequests('');
    } catch (error) {
      console.error('Booking error:', error);
      if (error.response?.status === 400) {
        toast.error(t('booking.errors.unavailable'));
      } else {
        toast.error(t('booking.errors.submitError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedPropertyData = properties.find(p => p.id === selectedProperty);

  return (
    <div data-testid="booking-page" className="min-h-screen pt-20 bg-white">
      <section className="bg-[#F9F6F0] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-2">
            {t('booking.overline')}
          </p>
          <h1
            data-testid="booking-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight text-[#1A1A1A] mb-4"
          >
            {t('booking.title')}
          </h1>
          <p className="text-base text-[#666666] max-w-2xl mx-auto leading-relaxed">
            {t('booking.subtitle')}
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                {t('booking.selectProperty')} *
              </label>
              <select
                data-testid="property-select"
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                className="w-full px-4 py-3 border border-[#2C5263]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5263] bg-white"
                required
              >
                <option value="">{t('booking.choosePlaceholder')}</option>
                {properties.map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.name} - {property.max_guests} {t('properties.guests')} - {t('properties.priceFrom')} €{property.price_low_season}{t('properties.perNight')}
                  </option>
                ))}
              </select>
            </div>

            {selectedProperty && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    {t('booking.checkIn')} *
                  </label>
                  <Calendar
                    data-testid="check-in-calendar"
                    onChange={setCheckIn}
                    value={checkIn}
                    minDate={new Date()}
                    tileDisabled={tileDisabled}
                    className="w-full"
                  />
                  {checkIn && (
                    <p className="mt-2 text-sm text-[#666666]">
                      {t('booking.checkIn')}: {checkIn.toLocaleDateString('it-IT')}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                    {t('booking.checkOut')} *
                  </label>
                  <Calendar
                    data-testid="check-out-calendar"
                    onChange={setCheckOut}
                    value={checkOut}
                    minDate={checkIn || new Date()}
                    tileDisabled={tileDisabled}
                    className="w-full"
                  />
                  {checkOut && (
                    <p className="mt-2 text-sm text-[#666666]">
                      {t('booking.checkOut')}: {checkOut.toLocaleDateString('it-IT')}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                {t('booking.numGuests')} *
              </label>
              <input
                type="number"
                data-testid="guests-input"
                value={guests}
                onChange={(e) => setGuests(parseInt(e.target.value))}
                min="1"
                max={selectedPropertyData?.max_guests || 10}
                className="w-full px-4 py-3 border border-[#2C5263]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5263]"
                required
              />
              {selectedPropertyData && (
                <p className="mt-1 text-xs text-[#666666]">
                  {t('booking.maxGuests')} {selectedPropertyData.max_guests} {t('booking.guestsFor')}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  {t('booking.nameLabel')} *
                </label>
                <input
                  type="text"
                  data-testid="guest-name-input"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  className="w-full px-4 py-3 border border-[#2C5263]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5263]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  {t('booking.emailLabel')} *
                </label>
                <input
                  type="email"
                  data-testid="guest-email-input"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-[#2C5263]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5263]"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                {t('booking.phoneLabel')} *
              </label>
              <input
                type="tel"
                data-testid="guest-phone-input"
                value={guestPhone}
                onChange={(e) => setGuestPhone(e.target.value)}
                className="w-full px-4 py-3 border border-[#2C5263]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5263]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                {t('booking.requestsLabel')}
              </label>
              <textarea
                data-testid="special-requests-textarea"
                value={specialRequests}
                onChange={(e) => setSpecialRequests(e.target.value)}
                rows="4"
                className="w-full px-4 py-3 border border-[#2C5263]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5263] resize-none"
                placeholder={t('booking.requestsPlaceholder')}
              />
            </div>

            <div className="text-center">
              <button
                type="submit"
                data-testid="submit-booking-button"
                disabled={loading}
                className="bg-[#2C5263] text-white px-12 py-4 rounded-md text-base font-medium hover:bg-[#1A3644] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('booking.submitting') : t('booking.submitButton')}
              </button>
              <p className="mt-4 text-sm text-[#666666]">
                {t('booking.confirmation')}
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
